import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Form } from 'react-bootstrap';
import { FaCompress, FaExpand, FaMinus, FaPlus, FaRedo } from 'react-icons/fa';
import { analizarDisposicionLectura } from '../../utils/readerLayout';
import './ReaderTool.scss';

const MIN_FONT_SIZE = 14;
const MAX_FONT_SIZE = 34;
const DEFAULT_FONT_SIZE = 18;
const DEFAULT_GUIDES = [9.25, 18.5, 27.75, 37, 46.25, 55.5, 64.75, 74, 83.25, 92.5];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const readStoredNumber = (key, fallback) => {
  if (typeof window === 'undefined') return fallback;

  const storedValue = Number.parseInt(window.localStorage.getItem(key), 10);
  return Number.isFinite(storedValue) ? storedValue : fallback;
};

const ReaderSurface = ({
  className = '',
  columnClass,
  fontSize,
  guidePositions,
  html,
  isRulerEnabled,
  layoutAnalysis,
  onGuidePointerDown,
}) => (
  <div className={`reader-tool__surface ${className}`}>
    <div className="lesson-document-shell reader-tool__document-shell">
      <div className={`reader-tool__document-frame reader-tool__document-frame--${layoutAnalysis.densidad} ${layoutAnalysis.usarDosColumnas ? 'reader-tool__document-frame--two-columns' : ''}`}>
        <div
          className={`lesson-document reader-tool__document ${columnClass}`}
          style={{
            '--reader-font-size': `${fontSize}px`,
            fontSize: `${fontSize}px`,
          }}
          dangerouslySetInnerHTML={{ __html: html || '' }}
        />
        {isRulerEnabled && (
          <div className="reader-tool__ruler-layer" aria-hidden="true">
            {guidePositions.map((position, index) => (
              <button
                className="reader-tool__ruler-guide"
                key={`guide-${index}`}
                onPointerDown={(event) => onGuidePointerDown(event, index)}
                style={{ left: `${position}%` }}
                tabIndex={-1}
                type="button"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

const ReaderTool = ({
  columnClass,
  html,
  storageKey = 'default',
}) => {
  const fontSizeStorageKey = `tecnica.reader.${storageKey}.fontSize`;
  const rulerStorageKey = `tecnica.reader.${storageKey}.ruler`;
  const [fontSize, setFontSize] = useState(() => clamp(
    readStoredNumber(fontSizeStorageKey, DEFAULT_FONT_SIZE),
    MIN_FONT_SIZE,
    MAX_FONT_SIZE
  ));
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [isRulerEnabled, setIsRulerEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(rulerStorageKey) === 'true';
  });
  const [guidePositions, setGuidePositions] = useState(DEFAULT_GUIDES);
  const dragState = useRef(null);
  const configuredColumns = columnClass === 'lesson-document--two-columns' ? 2 : 1;
  const layoutAnalysis = useMemo(
    () => analizarDisposicionLectura(html, configuredColumns),
    [configuredColumns, html]
  );
  const effectiveColumnClass = layoutAnalysis.usarDosColumnas
    ? 'lesson-document--two-columns'
    : 'lesson-document--one-column';

  const hasMinFontSize = fontSize <= MIN_FONT_SIZE;
  const hasMaxFontSize = fontSize >= MAX_FONT_SIZE;

  const controlsLabel = useMemo(() => `${fontSize}px`, [fontSize]);

  useEffect(() => {
    window.localStorage.setItem(fontSizeStorageKey, String(fontSize));
  }, [fontSize, fontSizeStorageKey]);

  useEffect(() => {
    window.localStorage.setItem(rulerStorageKey, String(isRulerEnabled));
  }, [isRulerEnabled, rulerStorageKey]);

  useEffect(() => {
    if (!isFocusMode) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousRootOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsFocusMode(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.documentElement.style.overflow = previousRootOverflow;
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFocusMode]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!dragState.current) return;

      const { index, rect } = dragState.current;
      const nextPosition = ((event.clientX - rect.left) / rect.width) * 100;
      setGuidePositions((currentPositions) => currentPositions.map((position, positionIndex) => (
        positionIndex === index ? clamp(nextPosition, 2, 98) : position
      )));
    };

    const handlePointerUp = () => {
      dragState.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);

  const decreaseFontSize = () => setFontSize((currentSize) => clamp(currentSize - 2, MIN_FONT_SIZE, MAX_FONT_SIZE));
  const increaseFontSize = () => setFontSize((currentSize) => clamp(currentSize + 2, MIN_FONT_SIZE, MAX_FONT_SIZE));

  const handleGuidePointerDown = (event, index) => {
    const frame = event.currentTarget.closest('.reader-tool__document-frame');
    if (!frame) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragState.current = {
      index,
      rect: frame.getBoundingClientRect(),
    };
  };

  const addGuide = () => {
    setGuidePositions((currentPositions) => {
      const nextPosition = currentPositions.length === 0
        ? 50
        : clamp(Math.max(...currentPositions) + 9.25, 2, 98);
      return [...currentPositions, nextPosition].sort((a, b) => a - b);
    });
  };

  const removeGuide = () => {
    setGuidePositions((currentPositions) => currentPositions.slice(0, -1));
  };

  const resetGuides = () => {
    setGuidePositions(DEFAULT_GUIDES);
  };

  const renderControls = (isFocusHeader = false) => (
    <div className={`reader-tool__controls ${isFocusHeader ? 'reader-tool__controls--focus' : ''}`}>
      <div className="reader-tool__control-group" aria-label="Zoom de lectura">
        <Button
          aria-label="Disminuir tamaño del texto"
          className="reader-tool__icon-button reader-tool__zoom-button"
          disabled={hasMinFontSize}
          onClick={decreaseFontSize}
          size="sm"
          type="button"
          variant="link"
        >
          <FaMinus />
        </Button>
        <span className="reader-tool__font-size">{controlsLabel}</span>
        <Button
          aria-label="Aumentar tamaño del texto"
          className="reader-tool__icon-button reader-tool__zoom-button"
          disabled={hasMaxFontSize}
          onClick={increaseFontSize}
          size="sm"
          type="button"
          variant="link"
        >
          <FaPlus />
        </Button>
      </div>

      <div className="reader-tool__control-group reader-tool__ruler-controls" aria-label="Regla de lectura">
        <Form.Check
          checked={isRulerEnabled}
          id={`reader-ruler-${storageKey}-${isFocusHeader ? 'focus' : 'normal'}`}
          label="Regla"
          onChange={(event) => setIsRulerEnabled(event.target.checked)}
          type="switch"
        />
        <Button
          aria-label="Agregar línea guía"
          className="reader-tool__icon-button reader-tool__ruler-button"
          disabled={!isRulerEnabled}
          onClick={addGuide}
          size="sm"
          type="button"
          variant="link"
        >
          <FaPlus />
        </Button>
        <Button
          aria-label="Quitar línea guía"
          className="reader-tool__icon-button reader-tool__ruler-button"
          disabled={!isRulerEnabled || guidePositions.length === 0}
          onClick={removeGuide}
          size="sm"
          type="button"
          variant="link"
        >
          <FaMinus />
        </Button>
        <Button
          aria-label="Restablecer líneas guía"
          className="reader-tool__icon-button reader-tool__ruler-button"
          disabled={!isRulerEnabled}
          onClick={resetGuides}
          size="sm"
          type="button"
          variant="link"
        >
          <FaRedo />
        </Button>
      </div>

      <Button
        className="reader-tool__focus-button"
        onClick={() => setIsFocusMode((currentMode) => !currentMode)}
        size="sm"
        type="button"
        variant={isFocusHeader ? 'light' : 'info'}
      >
        {isFocusHeader ? <FaCompress /> : <FaExpand />}
        <span>{isFocusHeader ? 'Salir del modo focus' : 'Modo Focus'}</span>
      </Button>
    </div>
  );

  return (
    <div className="reader-tool">
      {renderControls(false)}
      <ReaderSurface
        columnClass={effectiveColumnClass}
        fontSize={fontSize}
        guidePositions={guidePositions}
        html={html}
        isRulerEnabled={isRulerEnabled}
        layoutAnalysis={layoutAnalysis}
        onGuidePointerDown={handleGuidePointerDown}
      />

      {isFocusMode && createPortal(
        <div className="reader-tool__focus-overlay" role="dialog" aria-modal="true">
          <div className="reader-tool__focus-header">
            {renderControls(true)}
          </div>
          <div className="reader-tool__focus-content" tabIndex="0">
            <ReaderSurface
              className="reader-tool__surface--focus"
              columnClass={effectiveColumnClass}
              fontSize={fontSize}
              guidePositions={guidePositions}
              html={html}
              isRulerEnabled={isRulerEnabled}
              layoutAnalysis={layoutAnalysis}
              onGuidePointerDown={handleGuidePointerDown}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ReaderTool;
